import React, { useState, useEffect } from 'react';

import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Input,
  Spinner
} from 'reactstrap';

import { useForm, Controller } from 'react-hook-form';
import WhatsappBlockListService from '../../../services';
import ModalConfirmation from '../../../../../components/Modal/ConfirmationModal';
import { toast } from 'react-toastify';
import { ErrorMessage } from '@hookform/error-message';

function BlockingWordsModal(props) {
  const [isOpen, setIsOpen] = useState(false);
  const [blockedWords, setBlockedWords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [blockedWordId, setBlockedWordId] = useState(null);

  const addWordForm = useForm({
    defaultValues: {
      newWord: ''
    }
  });

  const editWordForm = useForm({
    defaultValues: {
      editWord: ''
    }
  });

  useEffect(() => {
    if (isOpen) {
      loadBlockedWords();
    }
  }, [isOpen]);

  const controlDeleteBlockedWord = (id) => {
    setBlockedWordId(id);
    setOpenModalConfirmation(true);
  };

  const loadBlockedWords = async () => {
    try {
      setLoading(true);
      const response = await WhatsappBlockListService.listBlockedWords();
      setBlockedWords(response.data.data || []);
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Erro ao carregar palavras bloqueadas',
        {
          autoClose: 3000,
          closeOnClick: true
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const onAddNewWord = async (data) => {
    try {
      setLoading(true);
      await WhatsappBlockListService.createBlockedWord({
        word: data.newWord.trim()
      });
      toast.success('Palavra adicionada com sucesso!', {
        autoClose: 3000,
        closeOnClick: true
      });
      addWordForm.reset({ newWord: '' });
      await loadBlockedWords();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao adicionar palavra', {
        autoClose: 3000,
        closeOnClick: true
      });
    } finally {
      setLoading(false);
    }
  };

  const onUpdateWord = async (data) => {
    try {
      setLoading(true);
      await WhatsappBlockListService.updateBlockedWord(editingId, {
        word: data.editWord.trim()
      });
      toast.success('Palavra atualizada com sucesso!', {
        autoClose: 3000,
        closeOnClick: true
      });
      setEditingId(null);
      editWordForm.reset({ editWord: '' });
      await loadBlockedWords();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao atualizar palavra', {
        autoClose: 3000,
        closeOnClick: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (word) => {
    setEditingId(word.id);
    editWordForm.setValue('editWord', word.word);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    editWordForm.reset({ editWord: '' });
  };

  const handleDelete = async (wordId) => {
    try {
      setLoading(true);
      await WhatsappBlockListService.deleteBlockedWord(wordId);
      toast.success('Palavra excluída com sucesso!', {
        autoClose: 3000,
        closeOnClick: true
      });
      await loadBlockedWords();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao excluir palavra', {
        autoClose: 3000,
        closeOnClick: true
      });
    } finally {
      setLoading(false);
      setOpenModalConfirmation(false);
    }
  };

  const handleModalToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      addWordForm.reset({ newWord: '' });
      editWordForm.reset({ editWord: '' });
      setEditingId(null);
    }
  };

  return (
    <>
      {props.buttonOpenModal({
        onClick: handleModalToggle
      })}
      <Modal
        className="modal-dialog-centered"
        size="lg"
        isOpen={isOpen}
        toggle={handleModalToggle}
        unmountOnClose={true}
      >
        <ModalHeader toggle={handleModalToggle}>
          <h3 className="modal-title">Gerenciar Palavras Bloqueadas</h3>
        </ModalHeader>
        <ModalBody>
          <div
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              paddingRight: '15px'
            }}
          >
            {blockedWords.map((word) => (
              <div key={word.id} className="mb-2">
                <Row className="align-items-center g-2">
                  <Col>
                    {editingId === word.id ? (
                      <Form onSubmit={editWordForm.handleSubmit(onUpdateWord)}>
                        <Row className="align-items-center g-2">
                          <Col>
                            <Controller
                              name="editWord"
                              control={editWordForm.control}
                              rules={{
                                required: 'Palavra é obrigatória!',
                                minLength: {
                                  value: 2,
                                  message: 'Mínimo de 2 caracteres'
                                }
                              }}
                              as={Input}
                              type="text"
                              placeholder="Digite a palavra para editar..."
                              className={
                                editWordForm.formState.errors.editWord
                                  ? 'is-invalid'
                                  : ''
                              }
                              disabled={loading}
                              defaultValue={word.word}
                            />
                            <ErrorMessage
                              errors={editWordForm.formState.errors}
                              name="editWord"
                              render={({ message }) => (
                                <div className="invalid-feedback d-block">
                                  {message}
                                </div>
                              )}
                            />
                          </Col>
                          <Col xs="auto">
                            <div className="d-flex gap-1">
                              <Button
                                color="success"
                                type="submit"
                                size="sm"
                                disabled={loading}
                                title="Confirmar Edição"
                              >
                                {loading ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <i className="fas fa-check"></i>
                                )}
                              </Button>
                              <Button
                                color="danger"
                                onClick={handleCancelEdit}
                                size="sm"
                                disabled={loading}
                                title="Cancelar"
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    ) : (
                      <>
                        <Input type="text" value={word.word} disabled />
                      </>
                    )}
                  </Col>
                  {editingId !== word.id && (
                    <Col xs="auto">
                      <div className="d-flex gap-1">
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => handleEdit(word)}
                          disabled={loading || editingId !== null}
                          title="Editar Palavra"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => controlDeleteBlockedWord(word.id)}
                          disabled={loading || editingId !== null}
                          title="Excluir Palavra"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            ))}

            {loading && blockedWords.length === 0 && (
              <div className="text-center py-3">
                <Spinner color="primary" />{' '}
                <p className="mt-2">Carregando...</p>
              </div>
            )}
            {!loading && blockedWords.length === 0 && (
              <div className="text-center py-3 text-muted">
                <i className="fas fa-inbox fa-2x mb-2"></i>
                <p>Nenhuma palavra bloqueada cadastrada.</p>
              </div>
            )}
          </div>

          <hr />

          <h5 className="mt-4">Adicionar Nova Palavra</h5>
          <Form onSubmit={addWordForm.handleSubmit(onAddNewWord)}>
            <Row className="align-items-start g-2">
              <Col>
                <Controller
                  name="newWord"
                  control={addWordForm.control}
                  rules={{
                    required: 'Palavra é obrigatória!',
                    minLength: { value: 2, message: 'Mínimo de 2 caracteres' }
                  }}
                  as={Input}
                  type="text"
                  placeholder="Digite a nova palavra..."
                  className={
                    addWordForm.formState.errors.newWord ? 'is-invalid' : ''
                  }
                  disabled={loading}
                  defaultValue=""
                />
                <ErrorMessage
                  errors={addWordForm.formState.errors}
                  name="newWord"
                  render={({ message }) => (
                    <div className="invalid-feedback d-block">{message}</div>
                  )}
                />
              </Col>
              <Col xs="auto">
                <Button
                  color="primary"
                  type="submit"
                  disabled={loading}
                  title="Adicionar Palavra"
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="fas fa-plus me-1"></i> Adicionar
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>
      <ModalConfirmation
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={blockedWordId}
        deleteResource={handleDelete}
        message={'Tem certeza que desbloquear excluir essa palavra?'}
      />
    </>
  );
}

export default BlockingWordsModal;
